/**
 * Bulk Action Bar Component.
 *
 * A floating bar that appears at the bottom of a list page when items
 * are selected. Shows the selection count and available bulk actions.
 * Styled to match the Apex Design System with a glass effect.
 */

import { X } from "lucide-react";
import Button from "@/components/ui/Button";

interface BulkAction {
  label: string;
  onClick: () => void;
  variant?: "primary" | "secondary" | "ghost" | "danger";
  isLoading?: boolean;
}

interface BulkActionBarProps {
  selectedCount: number;
  onClearSelection: () => void;
  actions: BulkAction[];
}

function BulkActionBar({
  selectedCount,
  onClearSelection,
  actions,
}: BulkActionBarProps) {
  if (selectedCount === 0) return null;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-scale-in">
      <div className="flex items-center gap-3 rounded-[var(--radius-xl)] elevation-3 px-5 py-3 shadow-[var(--shadow-xl)]">
        <span className="text-sm font-semibold text-[var(--text-primary)]">
          {selectedCount} selected
        </span>
        <div className="h-5 w-px bg-[var(--border-default)]" />
        {actions.map((action) => (
          <Button
            key={action.label}
            size="sm"
            variant={
              action.variant === "danger"
                ? "secondary"
                : action.variant || "secondary"
            }
            onClick={action.onClick}
            isLoading={action.isLoading}
            className={
              action.variant === "danger"
                ? "text-[var(--text-danger)] hover:bg-[var(--fill-danger)]"
                : ""
            }
          >
            {action.label}
          </Button>
        ))}
        <button
          onClick={onClearSelection}
          className="p-1 rounded-full text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-sunken)] transition-colors"
          title="Clear selection"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default BulkActionBar;
